import { cn } from "@/lib/utils";
import { CircleCheckIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Avatar, AvatarBadge, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import type { User } from "@/lib/swr";
import { Chip } from "@/components/badge/chip";

// Only the display fields, so id-shape variants (e.g. PickerUser) fit too.
type DisplayUser = Pick<User, "name" | "avatarThumb" | "kind">;

export const UserAvatar = ({
  user,
  className,
  badge,
  badgeClassName,
}: {
  user: DisplayUser;
  className?: string;
  badge?: ReactNode;
  badgeClassName?: string;
}) => (
  <Avatar className={cn("h-6 w-6", className)}>
    <SignedAvatarImage src={user.avatarThumb} alt={user.name} />
    <AvatarFallback className="text-xs">{user.name?.[0]}</AvatarFallback>
    {badge && (
      <AvatarBadge className={badgeClassName}>{badge}</AvatarBadge>
    )}
  </Avatar>
);

// Avatar + name, the standard way a person is displayed. When the person has
// confirmed (engagement sign-off), the avatar gives way to a green check.
// Bot accounts carry a small "bot" chip so they are never mistaken for people.
export const UserChip = ({
  user,
  confirmed,
  className,
}: {
  user: DisplayUser;
  confirmed?: boolean;
  className?: string;
}) => (
  <span className={cn("flex min-w-0 items-center gap-1.5", className)}>
    {confirmed ? (
      <CircleCheckIcon className="h-6 w-6 shrink-0 text-green-600" />
    ) : (
      <UserAvatar user={user} />
    )}
    <span className="min-w-0 truncate text-xs">{user.name}</span>
    {user.kind === "bot" && <Chip className="px-1">bot</Chip>}
  </span>
);
